import React, { useState, useEffect } from "react";
import { sbGetSetting, sbSetSetting } from "../utils/supabaseClient";
import { useToast } from "./Toast";
import "../styles/CreateTab.css";

const ResourceCreateTab = ({ onCreated }) => {
  const { success, error } = useToast();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [scheduled, setScheduled] = useState("");
  const [saving, setSaving] = useState(false);
  const [existingCategories, setExistingCategories] = useState([]);
  const [categoryMode, setCategoryMode] = useState("select");

  useEffect(() => {
    loadExistingCategories();
  }, []);

  const loadExistingCategories = async () => {
    try {
      const resources = await sbGetSetting("origamic-resources");
      if (Array.isArray(resources)) {
        const categories = [...new Set(resources.map(r => r.category).filter(Boolean))].sort();
        setExistingCategories(categories);
      }
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !url.trim()) {
      error("Title and URL are required");
      return;
    }

    // Validate URL
    try {
      new URL(url);
    } catch {
      error("Please enter a valid URL");
      return;
    }

    setSaving(true);
    try {
      const newResource = {
        id: `resource_${Date.now()}`,
        title: title.trim(),
        category: category.trim(),
        url: url.trim(),
        description: description.trim(),
        scheduled: scheduled ? new Date(scheduled).toISOString() : null,
        addedAt: new Date().toISOString(),
        draft: true,
      };

      let resources = await sbGetSetting("origamic-resources");
      if (!Array.isArray(resources)) resources = [];

      resources.push(newResource);
      await sbSetSetting("origamic-resources", resources);

      success("Resource saved as draft");
      setTitle("");
      setCategory("");
      setUrl("");
      setDescription("");
      setScheduled("");

      if (onCreated) {
        onCreated();
      }
    } catch (err) {
      console.error("Error saving resource:", err);
      error("Failed to save resource");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="create-container">
      <h2>Create New Resource</h2>

      <form onSubmit={handleSubmit} className="create-form">
        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., AI Research Papers"
            disabled={saving}
          />
        </div>

        <div className="form-group">
          <label>Category</label>
          {categoryMode === "select" ? (
            <select
              value={category}
              onChange={(e) => {
                if (e.target.value === "new") {
                  setCategoryMode("add");
                } else {
                  setCategory(e.target.value);
                }
              }}
              disabled={saving}
            >
              <option value="">-- Select Category --</option>
              {existingCategories.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
              <option value="new">+ Add New Category</option>
            </select>
          ) : (
            <>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Enter new category name..."
                disabled={saving}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setCategoryMode("select")}
                disabled={saving}
                style={{
                  marginTop: "8px",
                  padding: "8px 12px",
                  background: "#7A9CC4",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Back to Categories
              </button>
            </>
          )}
        </div>

        <div className="form-group">
          <label>URL *</label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com"
            disabled={saving}
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of the resource..."
            rows="4"
            disabled={saving}
          />
        </div>

        <div className="form-group">
          <label>Scheduled Date (Optional)</label>
          <input
            type="date"
            value={scheduled}
            onChange={(e) => setScheduled(e.target.value)}
            disabled={saving}
            title="Schedule when this resource should be published"
            style={{ width: "100%" }}
          />
          <small style={{ color: "#7A9CC4", marginTop: "4px", display: "block" }}>
            Optional: Schedule this resource to be published on a future date
          </small>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Save Resource"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResourceCreateTab;
